import React, { useState } from "react";
import { Link } from "react-router-dom";
import { SubscriptionUser } from "../types/subscription-user.entity";
import { UserSubscription } from "src/types/subscription.types";
import {
  FaUser,
  FaCalendarAlt,
  FaIdBadge,
  FaArrowLeft,
  FaStar,
  FaClock,
  FaTimesCircle,
  FaExclamationTriangle,
  FaChevronDown,
  FaChevronUp,
  FaStop,
} from "react-icons/fa";
import { useGetUserSubscriptionsQuery, useStopUserSubscriptionMutation } from "../subscription-users.service";
import { useGetPaymentTransactionsQuery } from "src/features/payment-transactions/payment-transactions.service";
import { useAppDispatch } from "src/store";
import { showToast } from "src/store/toast.store";
import Swal from "sweetalert2";
import {
  isStoreSubscriptionProvider,
  StoreSubscriptionVerificationModal,
  StoreSubscriptionVerificationTarget,
  useStoreSubscriptionVerification,
  VerifyStoreSubscriptionButton,
} from "./store-subscription-verification";
import {
  AuditStoreSubscriptionPaymentsButton,
  StoreSubscriptionPaymentAuditModal,
  useStoreSubscriptionPaymentAudit,
} from "./store-subscription-payment-audit";

type Props = {
  subscriptionUser: SubscriptionUser & { id: string };
  onBack?: () => void;
};


const statusClass = (status: string) => {
  switch (status) {
    case "ACTIVE":
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "CANCELLED":
      return "bg-red-100 text-red-700 border-red-200";
    case "EXPIRED":
      return "bg-gray-100 text-gray-700 border-gray-200";
    default:
      return "bg-amber-100 text-amber-700 border-amber-200";
  }
};

const providerClass = (provider: string) => {
  switch (provider) {
    case "TAALY":
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "APPLE":
      return "bg-gray-100 text-gray-700 border-gray-200";
    case "GOOGLE":
      return "bg-red-100 text-red-700 border-red-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

const formatDate = (ts: any) => new Date(ts?.toDate?.() || ts).toLocaleDateString();
const formatTime = (ts: any) =>
  new Date(ts?.toDate?.() || ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

// Determine base URL based on environment
const getOrganizationDashboardBaseUrl = () => {
  const projectId = process.env.REACT_APP_PROJECT_ID || "";
  const isDev = projectId.includes("dev") || process.env.NODE_ENV === "development";
  return isDev
    ? "https://taaly-organization-dashboard--taaly-dev.us-central1.hosted.app"
    : "https://taaly-organization-dashboard--taaly-prod.us-central1.hosted.app";
};

type SubscriptionCardProps = {
  subscription: UserSubscription & { id: string };
  userId: string;
  isVerifyingStoreSubscription: boolean;
  onVerifyStoreSubscription: (target: StoreSubscriptionVerificationTarget) => void;
  isAuditingStorePayments: boolean;
  onAuditStorePayments: (target: StoreSubscriptionVerificationTarget) => void;
};

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  subscription,
  userId,
  isVerifyingStoreSubscription,
  onVerifyStoreSubscription,
  isAuditingStorePayments,
  onAuditStorePayments,
}) => {
  const [isPaymentsExpanded, setIsPaymentsExpanded] = useState(false);
  const dispatch = useAppDispatch();
  const [stopSubscription, { isLoading: isStopping }] = useStopUserSubscriptionMutation();
  
  const isActive = subscription.status === "ACTIVE";
  const isExpired = subscription.status === "EXPIRED";
  
  const isInGracePeriod = subscription.isInGracePeriod;
  const graceUntil = subscription.graceUntil;

  // Check if this is a voucher-based subscription (not APPLE or GOOGLE)
  const isVoucherBased = subscription.provider === "TAALY";
  const storeProvider = isStoreSubscriptionProvider(subscription.provider)
    ? subscription.provider
    : undefined;

  // Fetch payment transactions related to this subscription by originalTransactionId = subscription.id
  const { data: transactions, isLoading: isLoadingTx } = useGetPaymentTransactionsQuery({
    where: { subscriptionDocId: subscription.id },
  } as any);

  const handleStopSubscription = async () => {
    const result = await Swal.fire({
      title: 'Stop Subscription?',
      text: `Are you sure you want to stop this subscription? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Stop Subscription',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        await stopSubscription({
          userId,
          subscriptionId: subscription.id,
        }).unwrap();
        
        dispatch(showToast({ message: "Subscription stopped successfully", type: "success" }));
        
        Swal.fire({
          title: 'Success!',
          text: 'Subscription has been stopped successfully.',
          icon: 'success',
          confirmButtonColor: '#10b981',
        });
      } catch (error: any) {
        const errorMessage = error?.data || error?.message || "Failed to stop subscription";
        dispatch(showToast({ message: errorMessage, type: "error" }));
        
        Swal.fire({
          title: 'Error!',
          text: errorMessage,
          icon: 'error',
          confirmButtonColor: '#ef4444',
        });
      }
    }
  };

  return (
    <div className={`rounded-xl shadow-lg border overflow-hidden transform transition-all duration-300 hover:scale-105 ${
      isActive 
        ? "border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50" 
        : isExpired 
        ? "border-gray-200 bg-gradient-to-br from-gray-50 to-slate-50"
        : "border-red-200 bg-gradient-to-br from-red-50 to-pink-50"
    }`}>
      {/* Header */}
      <div className={`px-6 py-4 border-b ${
        isActive ? "bg-emerald-100 border-emerald-200" : isExpired ? "bg-gray-100 border-gray-200" : "bg-red-100 border-red-200"
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FaStar className={`text-lg ${isActive ? "text-yellow-600" : isExpired ? "text-gray-600" : "text-red-600"}`} />
            <div>
              <h3 className="text-lg font-bold text-gray-800">{subscription.plan}</h3>
              <p className="text-sm text-gray-600">Subscription Plan</p>
            </div>
          </div>
            <div className="text-right">
              <span className={`px-3 py-1 rounded-full border text-xs font-medium ${statusClass(subscription.status)}`}>
                {subscription.status}
              </span>
            </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Provider Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            <FaStar className="text-yellow-500" />
            <span className="text-sm text-gray-600">Provider:</span>
            <span className={`px-2 py-1 rounded-full border text-xs font-medium ${providerClass(subscription.provider)}`}>
              {subscription.provider}
            </span>
            {/* Organization and Project Info - Clear but compact */}
            {(subscription.organizationId || subscription.projectId) && (
              <>
                <span className="text-gray-300 mx-1">•</span>
                {subscription.organizationId && (
                  <a
                    href={`${getOrganizationDashboardBaseUrl()}/en/organizations/${subscription.organizationId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-gray-600 font-mono font-medium hover:text-orange-600 transition-colors"
                  >
                    Org: <span className="text-orange-600 hover:underline">{subscription.organizationId}</span>
                  </a>
                )}
                {subscription.projectId && (
                  <a
                    href={`${getOrganizationDashboardBaseUrl()}/en/projects/${subscription.projectId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-gray-600 font-mono font-medium ml-2 hover:text-purple-600 transition-colors"
                  >
                    Proj: <span className="text-purple-600 hover:underline">{subscription.projectId}</span>
                  </a>
                )}
              </>
            )}
          </div>
          {/* paymentId removed */}
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="flex items-center gap-2">
            <FaCalendarAlt className="text-gray-500" />
            <div>
              <div className="text-sm text-gray-600">Start Date</div>
              <div className="font-semibold">{formatDate(subscription.startDate)}</div>
              <div className="text-xs text-gray-500">{formatTime(subscription.startDate)}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <FaCalendarAlt className="text-gray-500" />
            <div>
              <div className="text-sm text-gray-600">End Date</div>
              <div className="font-semibold">{formatDate(subscription.endDate)}</div>
              <div className="text-xs text-gray-500">{formatTime(subscription.endDate)}</div>
            </div>
          </div>
        </div>

        {/* Grace Period Warning */}
        {isInGracePeriod && graceUntil && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2">
              <FaExclamationTriangle className="text-amber-600" />
              <span className="text-sm font-medium text-amber-800">Grace Period Active</span>
            </div>
            <p className="text-xs text-amber-700 mt-1">
              Grace period until: {formatDate(graceUntil)} {formatTime(graceUntil)}
            </p>
          </div>
        )}

        {storeProvider && (
          <div className="mb-6">
            <VerifyStoreSubscriptionButton
              isLoading={isVerifyingStoreSubscription}
              onVerify={() =>
                onVerifyStoreSubscription({
                  userId,
                  subscriptionId: subscription.id,
                  provider: storeProvider,
                })
              }
            />
          </div>
        )}

        {/* Usage Counters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-lg font-bold text-blue-700">{subscription.bookedSessions.count}</div>
            <div className="text-xs text-blue-600">Booked Sessions</div>
            <div className="text-xs text-blue-500">{formatDuration(subscription.bookedSessions.durationInMin)}</div>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
            <div className="text-lg font-bold text-green-700">{subscription.directCalls.count}</div>
            <div className="text-xs text-green-600">Direct Calls</div>
            <div className="text-xs text-green-500">{formatDuration(subscription.directCalls.durationInMin)}</div>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-200">
            <div className="text-lg font-bold text-purple-700">{subscription.AISessions.count}</div>
            <div className="text-xs text-purple-600">AI Sessions</div>
            <div className="text-xs text-purple-500">{formatDuration(subscription.AISessions.durationInMin)}</div>
          </div>
        </div>

        {/* Stop Subscription Button (only for voucher-based subscriptions) */}
        {isVoucherBased && isActive && (
          <div className="mt-6 pt-4 border-t border-gray-200 flex justify-center">
            <button
              onClick={handleStopSubscription}
              disabled={isStopping}
              className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-red-600 disabled:hover:to-red-700 font-semibold text-sm shadow-md hover:shadow-lg transform hover:scale-[1.01] active:scale-[0.99] border border-red-500 hover:border-red-600"
            >
              <FaStop />
              <span>{isStopping ? "Stopping..." : "Stop Subscription"}</span>
            </button>
          </div>
        )}

        {/* Related Payment Transactions */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={() => setIsPaymentsExpanded(!isPaymentsExpanded)}
            className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-2 rounded-lg transition-colors duration-200"
          >
            <h4 className="text-sm font-semibold text-gray-700">
              Related Payments {transactions && transactions.length > 0 && `(${transactions.length})`}
            </h4>
            {isPaymentsExpanded ? (
              <FaChevronUp className="text-gray-500 text-xs" />
            ) : (
              <FaChevronDown className="text-gray-500 text-xs" />
            )}
          </button>
          
          <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
            isPaymentsExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}>
            <div className="pt-2">
              {isLoadingTx ? (
                <div className="text-gray-500 text-sm">Loading...</div>
              ) : transactions && transactions.length > 0 ? (
                <div className="max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400">
                  <div className="space-y-2 pr-2">
                    {transactions.map((t: any) => (
                      <div key={t.id} className="flex items-center justify-between p-2 rounded border bg-gray-50 text-sm">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/payment-transactions/${t.id}`}
                            className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 transform hover:scale-110 will-change-transform no-underline font-mono font-semibold text-[10px] sm:text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
                            title="View payment transaction details"
                          >
                            {t.id}
                          </Link>
                          <span className="px-2 py-0.5 rounded-full border text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                            {t.paymentStatus}
                          </span>
                        </div>
                        <div className="text-right text-gray-700">
                          <div className="font-semibold">{t.paymentAmount} {t.paymentCurrency}</div>
                          <div className="text-xs text-gray-500">{new Date(t.createdAt?.toDate?.() || t.createdAt).toLocaleString()}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-gray-500 text-sm">No related transactions</div>
              )}
            </div>
          </div>
          {storeProvider && (
            <div className="mt-4 flex justify-end">
              <AuditStoreSubscriptionPaymentsButton
                isLoading={isAuditingStorePayments}
                onAudit={() =>
                  onAuditStorePayments({
                    userId,
                    subscriptionId: subscription.id,
                    provider: storeProvider,
                  })
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const SubscriptionUserDetails: React.FC<Props> = ({ subscriptionUser, onBack }) => {
  const { data: userSubscriptions, isLoading: isLoadingSubscriptions } = useGetUserSubscriptionsQuery(subscriptionUser.id);
  const storeSubscriptionVerification = useStoreSubscriptionVerification();
  const storeSubscriptionPaymentAudit = useStoreSubscriptionPaymentAudit();

  // Filter out the active subscription from the list of all subscriptions
  const otherSubscriptions = userSubscriptions?.filter(
    subscription => subscription.id !== subscriptionUser.activeSubscriptionId
  ) || [];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Subscription User Details</h1>
          {onBack && (
            <button
              onClick={onBack}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-400 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-1"
            >
              <FaArrowLeft />
              Back
            </button>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            {/* User Info */}
            <div className="p-6 border-b border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center gap-3">
                <FaIdBadge className="text-gray-600" />
                <span className="font-semibold">User ID:</span>
                <Link
                  to={`/users/${subscriptionUser.id}`}
                  className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-blue-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-blue-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 transform hover:scale-110 will-change-transform no-underline font-mono font-semibold text-[10px] sm:text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
                  title="View user profile"
                >
                  {subscriptionUser.id}
                </Link>
              </div>

              <div className="flex items-center gap-3">
                <FaUser className="text-gray-600" />
                <span className="font-semibold">Name:</span>
                <span className="text-gray-700">{subscriptionUser.name}</span>
              </div>

              <div className="flex items-center gap-3">
                <span className="font-semibold">Email:</span>
                <span className="text-gray-700">{subscriptionUser.email}</span>
              </div>
            </div>

          {/* Active Subscription */}
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FaStar className="text-yellow-500" />
              Active Subscription
            </h2>
            {subscriptionUser.activeSubscription ? (
              <SubscriptionCard 
                subscription={{ ...subscriptionUser.activeSubscription, id: subscriptionUser.activeSubscriptionId }} 
                userId={subscriptionUser.id}
                isVerifyingStoreSubscription={storeSubscriptionVerification.isLoading}
                onVerifyStoreSubscription={storeSubscriptionVerification.verify}
                isAuditingStorePayments={storeSubscriptionPaymentAudit.isLoading}
                onAuditStorePayments={storeSubscriptionPaymentAudit.audit}
              />
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FaTimesCircle className="mx-auto text-4xl mb-2" />
                <p>No active subscription</p>
              </div>
            )}
          </div>

            {/* Other Plans */}
            <div className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FaClock className="text-blue-500" />
              Other Plans ({otherSubscriptions.length})
            </h2>
            
            {isLoadingSubscriptions ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading subscriptions...</p>
              </div>
            ) : otherSubscriptions.length > 0 ? (
              <div className="space-y-6">
                {otherSubscriptions.map((subscription) => (
                  <SubscriptionCard
                    key={subscription.id}
                    subscription={subscription}
                    userId={subscriptionUser.id}
                    isVerifyingStoreSubscription={storeSubscriptionVerification.isLoading}
                    onVerifyStoreSubscription={storeSubscriptionVerification.verify}
                    isAuditingStorePayments={storeSubscriptionPaymentAudit.isLoading}
                    onAuditStorePayments={storeSubscriptionPaymentAudit.audit}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FaTimesCircle className="mx-auto text-4xl mb-2" />
                <p>No other plans found</p>
              </div>
            )}
          </div>
          <StoreSubscriptionVerificationModal
            {...storeSubscriptionVerification.modalProps}
          />
          <StoreSubscriptionPaymentAuditModal
            {...storeSubscriptionPaymentAudit.modalProps}
          />
        </div>
      </div>
    </div>
  );
};

export default SubscriptionUserDetails;
